import React from 'react';
import { useQuery } from '@tanstack/react-query';
import useDepositStats from 'hooks/useDepositStats';
import DashboardSection from './components/DashboardSection';
import DashboardPage from './components/DashboardPage';
import PaidOperationsSummary from './components/PaidOperationsSummary';
import QuotaDistributionChart from './components/QuotaDistributionChart';
import FinanceDepositsCard from './components/FinanceDepositsCard';
import AgenciesStatsTable from './components/AgenciesStatsTable';
import BeninDepartmentsStats from './components/BeninDepartmentsStats';
import SelfRegistrationSummary from './components/SelfRegistrationSummary';
import useDashboardScope from './useDashboardScope';
import { useCampaignSwitchStatus } from 'hooks/useAGLOCampaign';
import HistoricalCampaignStrip from 'components/shared/HistoricalCampaignStrip';

import { statValue, readFinanceStats, readQuotaStats, readPaidOperations, readSelfRegistrationStats } from './stats';

const NationalDashboard = () => {
  const { api, campaignYear, enabled, queryOptions, scope, queryParams, isAGLO } = useDashboardScope();
  const quotaQuery = useQuery({
    ...queryOptions,
    queryKey: ['trip/stats/category/', ...scope, ...(isAGLO ? [queryParams] : [])],
    queryFn: api.list('trip/stats/category/', queryParams),
  });
  const coreQuery = useQuery({
    ...queryOptions,
    queryKey: ['trip/stats/default/', ...scope, ...(isAGLO ? [queryParams] : [])],
    queryFn: api.list('trip/stats/default/', queryParams),
  });
  const sexQuery = useQuery({
    ...queryOptions,
    queryKey: ['trip/stats/demographic/', ...scope, ...(isAGLO ? [queryParams] : [])],
    queryFn: api.list('trip/stats/demographic/', queryParams),
  });
  const agenciesQuery = useQuery({
    ...queryOptions,
    queryKey: ['trip/trip_report/', ...scope, ...(isAGLO ? [queryParams] : [])],
    queryFn: api.list('trip/trip_report/', queryParams),
  });
  const selfRegistrationQuery = useQuery({
    ...queryOptions,
    enabled: enabled && isAGLO,
    queryKey: ['trip/stats/self_registered/', ...scope, ...(isAGLO ? [queryParams] : [])],
    queryFn: api.list('trip/stats/self_registered/', queryParams),
  });
  const departmentsQuery = useQuery({
    ...queryOptions,
    queryKey: ['trip/stats/department/', ...scope, ...(isAGLO ? [queryParams] : [])],
    queryFn: api.list('trip/stats/department/', queryParams),
  });
  const financeQuery = useDepositStats(isAGLO);
  const quota = readQuotaStats(quotaQuery.data?.data?.data);
  const core = coreQuery.data?.data?.data;
  const sexStats = sexQuery.data?.data?.data;
  const sexValues = {
    male: statValue(sexStats, 'Hommes'),
    female: statValue(sexStats, 'Femmes'),
    unspecified: statValue(sexStats, 'Sexe non renseigné'),
  };
  const sex = Object.values(sexValues).every(Number.isFinite) ? sexValues : null;
  const agencies = agenciesQuery.data?.data;
  const selfRegistration = readSelfRegistrationStats(selfRegistrationQuery.data?.data?.data);
  const departments = departmentsQuery.data?.data;
  const finance = readFinanceStats(financeQuery.data?.data?.data);
  const operations = readPaidOperations(core, true);
  const queries = [
    quotaQuery,
    coreQuery,
    sexQuery,
    agenciesQuery,
    departmentsQuery,
    financeQuery,
    ...(isAGLO ? [selfRegistrationQuery] : []),
  ];
  useCampaignSwitchStatus(queries);
  const hasCompleteData = !!(quota && operations && sex && agencies && departments && finance);
  const sexRows = sex
    ? [
        { label: 'Hommes', value: sex.male, color: 'bg-emerald-700' },
        { label: 'Femmes', value: sex.female, color: 'bg-emerald-400' },
        { label: 'Sexe non renseigné', value: sex.unspecified, color: 'bg-slate-300' },
      ]
    : [];
  const sexTotal = sexRows.reduce((sum, row) => sum + row.value, 0);
  return (
    <DashboardPage
      ariaLabel="Tableau de bord national"
      headerContent={<HistoricalCampaignStrip />}
      title="Tableau de bord"
      subtitle={`Campagne ${campaignYear || 'indisponible'}`}
      enabled={enabled}
      queries={queries}
      hasCompleteData={hasCompleteData}
      contentClassName="space-y-4"
    >
      <DashboardSection
        title="Quota de la campagne"
        help="Seuls les pèlerins soldés occupent une place. Un acompte ne réserve pas de place."
        query={quotaQuery}
        available={!!quota}
      >
        {quota && <QuotaDistributionChart quota={quota} />}
      </DashboardSection>
      <div className="grid auto-rows-fr items-stretch gap-4 md:grid-cols-2 xl:grid-cols-[1fr_1.3fr_1.3fr]">
        <DashboardSection title="Répartition des soldés" query={sexQuery} available={!!sex}>
          {sex && (
            <>
              <div className="mb-4 flex h-1.5 overflow-hidden rounded-sm bg-slate-100" aria-hidden="true">
                {sexRows.map((row) => (
                  <div
                    key={row.label}
                    className={row.color}
                    style={{ width: `${sexTotal ? (row.value / sexTotal) * 100 : 0}%` }}
                  />
                ))}
              </div>
              <dl className="space-y-3">
                {sexRows.map((row) => (
                  <div key={row.label} className="flex items-center justify-between gap-4 text-sm">
                    <dt className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-sm ${row.color}`} />
                      {row.label}
                    </dt>
                    <dd className="font-semibold">{row.value.toLocaleString('fr-FR')}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </DashboardSection>
        <DashboardSection title="Suivi opérationnel" query={coreQuery} available={!!operations}>
          {operations && (
            <PaidOperationsSummary
              operations={operations}
              help="Aptitude et passeport suivis sur la base des pèlerins soldés."
            />
          )}
        </DashboardSection>
        <DashboardSection title="Finance" query={financeQuery} available={!!finance}>
          {finance && <FinanceDepositsCard confirmed={finance.confirmed} pending={finance.pending} />}
        </DashboardSection>
      </div>
      <DashboardSection
        title="Situation par société"
        query={agenciesQuery}
        available={Array.isArray(agencies)}
        headerContent={isAGLO && selfRegistration ? <SelfRegistrationSummary {...selfRegistration} /> : null}
      >
        {Array.isArray(agencies) && <AgenciesStatsTable stats={agencies} />}
      </DashboardSection>
      <DashboardSection
        title="Répartition par département"
        query={departmentsQuery}
        available={Array.isArray(departments)}
      >
        {Array.isArray(departments) && (
          <BeninDepartmentsStats stats={departments} paidBase={!coreQuery.isError ? operations?.base : null} />
        )}
      </DashboardSection>
    </DashboardPage>
  );
};

export default NationalDashboard;
